import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../Icon";
import MockFormModal from "./MockFormModal";
import { matchesMockSearch } from "./mockData";

const ROLES = ["Agent", "Admin"];
const EMPTY_FORM = { name: "", email: "", title: "", role: "Agent" };

const MockUsers = ({ users, onAddUser, onUpdateUser, onDeleteUser, showConfirm, searchQuery = "" }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState("");
    const nameRef = useRef(null);

    const filteredUsers = useMemo(
        () => users.filter((u) => matchesMockSearch(searchQuery, u.name, u.email, u.title, u.role, u.joined)),
        [users, searchQuery]
    );

    useEffect(() => {
        if (showModal && nameRef.current) {
            nameRef.current.focus();
        }
    }, [showModal, editingId]);

    const resetForm = () => {
        setForm(EMPTY_FORM);
        setError("");
        setEditingId(null);
        setShowModal(false);
    };

    const openAddModal = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setError("");
        setShowModal(true);
    };

    const openEditModal = (user) => {
        setEditingId(user.id);
        setForm({
            name: user.name,
            email: user.email,
            title: user.title,
            role: user.role,
        });
        setError("");
        setShowModal(true);
    };

    const handleSave = () => {
        const trimmedName = form.name.trim();
        const trimmedEmail = form.email.trim().toLowerCase();
        if (!trimmedName) {
            setError("Enter a name.");
            return;
        }
        if (!trimmedEmail || !trimmedEmail.includes("@")) {
            setError("Enter a valid email address.");
            return;
        }
        const duplicate = users.some(
            (u) => u.email.toLowerCase() === trimmedEmail && u.id !== editingId
        );
        if (duplicate) {
            setError("A user with this email already exists.");
            return;
        }

        if (editingId) {
            onUpdateUser(editingId, {
                name: trimmedName,
                email: trimmedEmail,
                title: form.title.trim() || "Agent",
                role: form.role,
            });
        } else {
            onAddUser({
                name: trimmedName,
                email: trimmedEmail,
                title: form.title.trim() || "Agent",
                role: form.role,
            });
        }
        resetForm();
    };

    const handleDelete = (user) => {
        if (users.length <= 1) {
            return;
        }
        showConfirm?.({
            title: "Are you sure you want to remove this user from workspace?",
            message: "You can add them to the workspace via email later.",
            onConfirm: () => {
                onDeleteUser(user.id);
                if (editingId === user.id) resetForm();
            },
        });
    };

    return (
        <div className="clk-mk-page clk-mk-page--users">
            <div className="clk-mk-page-header-row">
                <h5>Users</h5>
                <button type="button" className="clk-mk-primary-btn" onClick={openAddModal}>
                    <img src="/images/plus.svg" alt="" aria-hidden="true" />
                    Add User
                </button>
            </div>

            {showModal && (
                <MockFormModal
                    title={editingId ? "Edit user" : "New user"}
                    onClose={resetForm}
                >
                    <label className="clk-mk-field">
                        <span>Name</span>
                        <input
                            ref={nameRef}
                            type="text"
                            value={form.name}
                            placeholder="e.g. Alex Morgan"
                            onChange={(e) => {
                                const { value } = e.target;
                                setForm((f) => ({ ...f, name: value }));
                                setError("");
                            }}
                        />
                    </label>
                    <label className="clk-mk-field">
                        <span>Email</span>
                        <input
                            type="email"
                            value={form.email}
                            placeholder="alex@company.com"
                            onChange={(e) => {
                                const { value } = e.target;
                                setForm((f) => ({ ...f, email: value }));
                                setError("");
                            }}
                        />
                    </label>
                    <label className="clk-mk-field">
                        <span>Title</span>
                        <input
                            type="text"
                            value={form.title}
                            placeholder="e.g. Support Agent"
                            onChange={(e) => {
                                const { value } = e.target;
                                setForm((f) => ({ ...f, title: value }));
                            }}
                        />
                    </label>
                    <label className="clk-mk-field">
                        <span>Role</span>
                        <select
                            value={form.role}
                            onChange={(e) => {
                                const { value } = e.target;
                                setForm((f) => ({ ...f, role: value }));
                            }}
                        >
                            {ROLES.map((r) => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                    </label>
                    {error && <p className="clk-mk-form-error">{error}</p>}
                    <div className="clk-mk-create-actions">
                        <button type="button" className="clk-mk-link-btn" onClick={resetForm}>
                            Cancel
                        </button>
                        <button type="button" className="clk-mk-primary-btn" onClick={handleSave}>
                            {editingId ? "Save changes" : "Add user"}
                        </button>
                    </div>
                </MockFormModal>
            )}

            <div className="clk-mk-table-card">
                {filteredUsers.length === 0 ? (
                    <p className="clk-mk-empty">No users match your search.</p>
                ) : (
                    <table className="clk-mk-app-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Title</th>
                                <th>Email</th>
                                <th>Role</th>
                                <th>Accepted</th>
                                <th>Joined Since</th>
                                <th className="clk-mk-col-actions" aria-label="Actions">
                                    <span className="clk-mk-sr-only">Actions</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map((u) => (
                                <tr key={u.id}>
                                    <td>{u.name}</td>
                                    <td>{u.title}</td>
                                    <td>{u.email}</td>
                                    <td>{u.role}</td>
                                    <td>
                                        {u.accepted && (
                                            <span className="clk-mk-accepted" aria-label="Accepted">
                                                <Icon name="check" size={10} stroke={2.5} />
                                            </span>
                                        )}
                                    </td>
                                    <td>{u.joined}</td>
                                    <td className="clk-mk-col-actions">
                                        <div className="clk-mk-row-actions">
                                            <button
                                                type="button"
                                                className="clk-mk-icon-btn"
                                                aria-label={`Edit ${u.name}`}
                                                onClick={() => openEditModal(u)}
                                            >
                                                <Icon name="pencil" size={13} />
                                            </button>
                                            <button
                                                type="button"
                                                className="clk-mk-icon-btn clk-mk-icon-btn--danger"
                                                aria-label={`Delete ${u.name}`}
                                                onClick={() => handleDelete(u)}
                                            >
                                                <Icon name="trash" size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="clk-mk-table-footer clk-mk-table-footer--simple">
                <div className="clk-mk-pagination">
                    <button type="button" className="clk-mk-page-btn is-active">1</button>
                </div>
                <span className="clk-mk-results-count">
                    Showing {filteredUsers.length} of {users.length}
                </span>
            </div>
        </div>
    );
};

export default MockUsers;
